import { PITCH, type MatchState, type SimConfig, type SimEvent, type TeamIdx, type TeamSize } from '@fafi/shared';

// D138/D139 (§8) — the training ground.
//
// A LESSON extends the D128 drill shape with ordered sub-goals. Everything here is DATA and PURE
// FUNCTIONS: `setup` mutates a MatchState by composing shared exports, and `detect` is a pure function
// over (SimEvents, MatchState, memo). No I/O, no wall clock, no `Math.random` — the purity fence covers
// this directory from D138 on (eslint PURE_SIM_DIRS + the purity.test.ts sweep), because a lesson that
// is not deterministic is a lesson that cannot be tested or replayed.
//
// The psychology the user asked for lives in the SHAPE, not in the copy: one skill per step, every step
// individually detectable and celebrated, attempts free and progress permanent.

/** What a detector reports about its step this tick. */
export interface Progress {
  /** How many times the goal has been met so far (for a `count` goal); 0/1 for a plain one. */
  readonly done: number;
  /** Opaque per-step scratch, carried tick to tick. Survives an auto-reset, dies on step advance. */
  readonly memo?: unknown;
}

export interface LessonStep {
  readonly id: string;
  /**
   * On-screen coaching. `{key:action}` placeholders resolve against the player's LIVE bindings, so the
   * copy is rebind-proof: a lesson never tells an AZERTY player to press W.
   */
  readonly instruction: string;
  readonly goal: {
    /** Repetitions required (default 1) — drives the "3/5" progress chips. */
    readonly count?: number;
    /** D151: `seat` is the entity the HUMAN is driving. A detector must be about him — before this the
     *  helpers were team-scoped, so a bot's pass, kick or goal completed your step for you. */
    readonly detect: (events: readonly SimEvent[], state: MatchState, memo: unknown, seat: number) => Progress;
  };
  /** Rotated deterministically (by attempt index), never randomly. */
  readonly praise: readonly string[];
  /**
   * D153 — this attempt has FAILED: serve the situation again immediately rather than letting a match
   * break out. Attempts are free and progress is permanent, so a failure costs nothing but the reset.
   * Per STEP, not per lesson, because failure means opposite things in the two halves of a 1v1: while
   * you are attacking, losing the ball ends it; while you are defending, HIM getting past you does.
   */
  readonly failOn?: (events: readonly SimEvent[], state: MatchState, seat: number) => boolean;
}

export interface LessonSpec {
  readonly id: string;
  readonly title: string;
  readonly blurb: string;
  readonly icon: string;
  readonly teamSize: TeamSize;
  /** Which side runs as bots — the D128 DrillSpec ancestry. */
  readonly bots: 'defense' | 'both';
  readonly playerTeam: TeamIdx;
  /** D143 — which player the human drives from the first tick. `'gk'` seats you in goal and LOCKS the
   *  router there, so auto-switching cannot quietly hand you an outfielder mid-lesson; keeper-week's
   *  whole premise is that you are the keeper. Default `'outfield'`. */
  readonly playerRole?: 'gk' | 'outfield';
  /** D152 — bots may SHOOT in this lesson. Default false: a rehearsal is not a match, and you are the
   *  only one who scores in your own lesson. `keeper-week` opts in, because a penalty at your goal is
   *  the entire point of it. */
  readonly botsShoot?: boolean;
  /** Set the board up. Mutates `state`; must stay pure and deterministic. */
  setup(state: MatchState, cfg: SimConfig): void;
  readonly steps: readonly LessonStep[];
  /** When the board re-arms itself. Default 'dead-ball'. */
  readonly resetOn?: 'dead-ball' | 'outcome' | 'never';
  /** The optional "★ sharp" objective (PT4-P17) — one per lesson, no grades beyond ✓ and ★. */
  readonly bonus?: LessonStep;
  /** Render-only markers (cones, gates, target zones). Objectives read POSITIONS, never these. */
  readonly props?: readonly LessonProp[];
}

export interface LessonProp {
  readonly kind: 'gate' | 'cone' | 'zone';
  readonly x: number;
  readonly y: number;
  /** Gate width / zone radius, in metres. */
  readonly r: number;
}

// ── detector helpers (pure; shared by the wave-1 lessons) ──────────────────────────────────────────

export const NONE: Progress = { done: 0 };

/** Count SimEvents matching `pred` this tick, on top of `memo`'s running total. */
export function countEvents(events: readonly SimEvent[], memo: unknown, pred: (e: SimEvent) => boolean): Progress {
  const prev = typeof memo === 'number' ? memo : 0;
  let n = prev;
  for (const e of events) if (pred(e)) n += 1;
  return { done: n, memo: n };
}

/** A goal scored BY `team` (never an own goal credited to them — that is not the skill being taught). */
export const scoredBy = (team: TeamIdx) => (e: SimEvent): boolean => e.t === 'GOAL' && e.team === team && !e.ownGoal;

/** The controlled team gained possession, for any reason. */
export const gainedBy = (ids: ReadonlySet<number>) => (e: SimEvent): boolean => e.t === 'POSSESSION' && e.entity !== null && ids.has(e.entity);

/** Did `player` pass through the gate at `(x, y)` with half-width `r` this tick? Latched in the memo so
 *  a player loitering inside a gate cannot farm it. */
export function throughGate(state: MatchState, id: number, gate: LessonProp, memo: unknown): Progress {
  const st = (typeof memo === 'object' && memo !== null ? memo : {}) as { n?: number; inside?: boolean };
  const p = state.players[id];
  const n = st.n ?? 0;
  if (!p) return { done: n, memo: st };
  const inside = Math.hypot(p.pos.x - gate.x, p.pos.y - gate.y) <= gate.r;
  const crossed = inside && st.inside !== true;
  return { done: n + (crossed ? 1 : 0), memo: { n: n + (crossed ? 1 : 0), inside } };
}

/** The pitch, for a lesson's own geometry. */
export const pitchOf = (s: MatchState): (typeof PITCH)[TeamSize] => PITCH[s.mode.teamSize];
